import { useCallback, useEffect, useRef, useState } from "react";

const isOverflowing = (element: HTMLElement): boolean =>
  element.scrollWidth - element.clientWidth > 1 ||
  element.scrollHeight - element.clientHeight > 1;

/**
 * Reports whether an element's content is bigger than the box it was given, so
 * callers can show the full text some other way. The browser truncates in CSS
 * without telling React, so the only way to know is to measure the node.
 *
 * Pass the rendered content so the measurement is redone when it changes: the
 * box keeps its size when only the text changes, so nothing else would fire.
 */
const useIsTruncated = <T extends HTMLElement>(content?: unknown) => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    setIsTruncated(isOverflowing(element));
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, content]);

  return { ref, isTruncated };
};

export default useIsTruncated;
